"use client";

import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@ui/components/card";
import {
  ChartContainer,
  type ChartConfig,
} from "@ui/components/chart";
import { TrendingUpIcon, ActivityIcon, CalendarIcon, PercentIcon } from "lucide-react";

interface TrafficDataPoint {
  date: string;
  trafficPercentage: number;
  visitCount: number;
}

interface BotTrafficStats {
  dailyData: TrafficDataPoint[];
  totalVisits: number;
  avgPercentage: number;
  peakPercentage: number;
  peakDate: string;
  totalTrafficInPeriod: number;
}

interface BotTrafficChartProps {
  botName: string;
  trafficStats: BotTrafficStats | null;
  className?: string;
}

export function BotTrafficChart({ botName, trafficStats, className }: BotTrafficChartProps) {
  // Format chart data with readable date labels
  const chartData = useMemo(() => {
    if (!trafficStats?.dailyData) return [];
    
    return trafficStats.dailyData.map(item => {
      const date = new Date(item.date);
      return {
        ...item,
        dateLabel: date.toLocaleDateString(undefined, { 
          month: 'short', 
          day: 'numeric' 
        }),
        fullDate: item.date,
      };
    });
  }, [trafficStats]);

  // Calculate additional insights
  const insights = useMemo(() => {
    if (!chartData.length) return null;

    // Find trend (compare last 7 days to previous 7 days)
    const recentData = chartData.slice(-7);
    const previousData = chartData.slice(-14, -7);
    
    const recentAvg = recentData.length > 0 
      ? recentData.reduce((sum, d) => sum + d.trafficPercentage, 0) / recentData.length 
      : 0;
    const previousAvg = previousData.length > 0 
      ? previousData.reduce((sum, d) => sum + d.trafficPercentage, 0) / previousData.length 
      : 0;
    
    const trendPercentage = previousAvg > 0 
      ? ((recentAvg - previousAvg) / previousAvg) * 100 
      : 0;

    // Find days with activity
    const activeDays = chartData.filter(d => d.visitCount > 0).length;
    const activityRate = (activeDays / chartData.length) * 100;

    return {
      trendPercentage,
      trendDirection: trendPercentage > 5 ? 'up' : trendPercentage < -5 ? 'down' : 'stable',
      activeDays,
      totalDays: chartData.length,
      activityRate,
    };
  }, [chartData]);

  const chartConfig = {
    trafficPercentage: {
      label: `${botName} traffic`,
      color: "var(--chart-1)",
    },
  } satisfies ChartConfig;

  // If no traffic data, show placeholder
  if (!trafficStats || !chartData.length) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ActivityIcon className="h-5 w-5" />
            Traffic Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-muted-foreground bg-muted/50 rounded-lg">
            <div className="text-center">
              <ActivityIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No traffic data available yet</p>
              <p className="text-sm">Check back later for activity insights</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ActivityIcon className="h-5 w-5" />
          {botName} Traffic (Last 90 Days)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Chart */}
        <ChartContainer config={chartConfig} className="aspect-auto h-48 w-full">
            <AreaChart
              data={chartData}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <defs>
                <linearGradient id="trafficGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-trafficPercentage)" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="var(--color-trafficPercentage)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
              <XAxis 
                dataKey="dateLabel" 
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value.toFixed(2)}%`}
                width={50}
              />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const date = new Date(data.fullDate);
                    return (
                      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
                        <p className="font-medium text-sm">
                          {date.toLocaleDateString(undefined, { 
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-[var(--color-trafficPercentage)] rounded-full" />
                            <span className="text-sm">
                              {botName}: {data.trafficPercentage.toFixed(3)}%
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {data.visitCount.toLocaleString()} visits
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="trafficPercentage"
                stroke="var(--color-trafficPercentage)"
                strokeWidth={2}
                fill="url(#trafficGradient)"
              />
            </AreaChart>
        </ChartContainer>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <PercentIcon className="h-3 w-3" />
              <span className="text-xs">Avg Share</span>
            </div>
            <div className="text-lg font-semibold text-foreground">
              {trafficStats.avgPercentage.toFixed(3)}%
            </div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <TrendingUpIcon className="h-3 w-3" />
              <span className="text-xs">Peak</span>
            </div>
            <div className="text-lg font-semibold text-foreground">
              {trafficStats.peakPercentage.toFixed(3)}%
            </div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <ActivityIcon className="h-3 w-3" />
              <span className="text-xs">Total Visits</span>
            </div>
            <div className="text-lg font-semibold">
              {trafficStats.totalVisits.toLocaleString()}
            </div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <CalendarIcon className="h-3 w-3" />
              <span className="text-xs">Active Days</span>
            </div>
            <div className="text-lg font-semibold">
              {insights?.activeDays || 0}/{insights?.totalDays || 90}
            </div>
          </div>
        </div>

        {/* Trend Indicator */}
        {insights && insights.trendDirection !== 'stable' && (
          <div className="mt-3 pt-3 border-t">
            <div className={`text-sm ${
              insights.trendDirection === 'up' 
                ? 'text-positive' 
                : 'text-negative'
            }`}>
              {insights.trendDirection === 'up' ? '↑' : '↓'} {Math.abs(insights.trendPercentage).toFixed(1)}% vs previous week
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
